import { useCallback, useEffect } from 'react';

import { TaskTitleField } from './TaskTitleField';

interface TaskTitleSectionProps {
  title: string;
  /** A staged note's header shows no identity, so its title stays inline. */
  showInlineTitle: boolean;
  /** Driven by the slideout header's "Edit task" action. */
  editMode: boolean;
  onSaveTitle?: (value: string) => Promise<void>;
  onEditModeChange?: (isEditMode: boolean) => void;
}

/**
 * Title rendering for the task overview: a staged note keeps its title
 * inline (its header shows no name); a real task never repeats its name
 * (the record header already shows it) and instead reveals an editor here
 * only while the header's "Edit task" action is active.
 */
export function TaskTitleSection({
  title,
  showInlineTitle,
  editMode,
  onSaveTitle,
  onEditModeChange,
}: TaskTitleSectionProps) {
  // The header "Edit task" action opens the title editor. When the title can't
  // be edited (no permission / no user), there is nothing to open — hand the
  // flag straight back so the header action doesn't stay hidden behind an
  // edit mode with no editor in it.
  const showTitleEditor =
    !showInlineTitle && editMode && onSaveTitle !== undefined;
  useEffect(() => {
    if (!showInlineTitle && editMode && onSaveTitle === undefined) {
      onEditModeChange?.(false);
    }
  }, [showInlineTitle, editMode, onSaveTitle, onEditModeChange]);
  const handleTitleEditingEnd = useCallback(() => {
    onEditModeChange?.(false);
  }, [onEditModeChange]);

  return (
    <>
      {/* A staged note keeps its title inline (its header shows no name). */}
      {showInlineTitle && <TaskTitleField title={title} onSave={onSaveTitle} />}

      {/* Read mode never repeats a task's name: the record header already
          shows it. The editor appears here (in place of where a duplicate
          would sit) only while the header's "Edit task" action is active. */}
      {showTitleEditor && (
        <TaskTitleField
          title={title}
          onSave={onSaveTitle}
          forceEditing
          onEditingEnd={handleTitleEditingEnd}
        />
      )}
    </>
  );
}
